import {
  GlossaryRow,
  INVALID_ROW_REASON,
  MAX_ENTRIES_PER_LANGUAGE,
  SPEECHMATICS_MAX_WORD_LENGTH,
  SPEECHMATICS_MAX_WORDS_PER_ELEMENT,
  UPLOAD_WARNING_TYPE,
  UploadWarning,
  WarningRow,
} from '../constants';
import { TEMPLATE_HEADER_ROWS } from '../constants/glossaryHeaders';
import {
  GlossaryScript,
  SCRIPT_BY_LANGUAGE,
} from '../constants/glossaryLanguages';

/**
 * CSV 行パーサ。
 * カンマ・タブのいずれかをデリミタとして受け取り、
 * ダブルクォートで囲まれたフィールドと、クォート内のエスケープ（`""`→`"`）を処理する。
 * ※ クォート内改行には対応していない（先に split(/\r?\n/) しているため）。
 */
const splitCsvLine = (line: string, delimiter: ',' | '\t' = ','): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const ch = line[i];

    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i += 2;
      } else if (ch === '"') {
        inQuotes = false;
        i += 1;
      } else {
        current += ch;
        i += 1;
      }
    } else if (ch === '"' && current === '') {
      // RFC 4180: 引用符はフィールドの先頭（current が空）でのみ引用開始とみなす。
      // フィールド途中の " は通常の文字として後続の else ブランチで処理する。
      inQuotes = true;
      i += 1;
    } else if (ch === delimiter) {
      result.push(current);
      current = '';
      i += 1;
    } else {
      current += ch;
      i += 1;
    }
  }

  result.push(current);

  return result;
};

/**
 * CSV テキストの先頭行からデリミタを自動検出する。
 * クォート外に出現するタブ文字が含まれる場合はタブ区切り、それ以外はカンマ区切りとみなす。
 * クォート内のタブ（フィールド値の一部）は判定に含めない。
 */
const detectDelimiter = (firstLine: string): ',' | '\t' => {
  let inQuotes = false;

  for (let i = 0; i < firstLine.length; i += 1) {
    const ch = firstLine[i];

    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === '\t' && !inQuotes) {
      return '\t';
    }
  }

  return ',';
};

/** パース中間形式（行番号付き生データ） */
type ParsedRow = {
  rowNum: number;
  language: string;
  content: string;
  soundsLike: string[];
};

// ---------------------------------------------------------------------------
// 共通ユーティリティ
// ---------------------------------------------------------------------------

/**
 * 不可視スペース類（U+3000 全角スペース・U+00A0 NBSP）を半角スペース（U+0020）へ正規化する。
 * どちらもユーザーがスペースと区別できないまま入力・コピペしやすく、
 * SCRIPT_PATTERNS の各スクリプトパターンが許可しないため、
 * 解析前に正規化してサイレントエラーを防ぐ。
 * タブや通常の半角スペースはここでは変換しない（クォート内タブは CSV 仕様上有効）。
 */
const normalizeSpaces = (s: string): string =>
  s.replace(/[\u3000\u00A0]/g, ' ');

/**
 * 文字列を空白文字区切りで語に分割する。
 * 呼び出し前に normalizeSpaces() で全角スペース・NBSP は半角へ正規化済み。
 * それ以外の空白類（U+202F など）は正規化対象外だが、
 * ここで区切りとして数えるため語数はむしろ厳しめに出る（Speechmatics 側で
 * 通るものを弾く方向なので、無言で drop される事故にはならない）。
 */
const splitWords = (s: string): string[] => s.trim().split(/\s+/);

/**
 * 警告行（WarningRow）を組み立てる共通ヘルパー。
 * content と soundsLike は加工せずそのまま渡す。
 * 表示の折り返し・省略は CSS（text-overflow: ellipsis）と
 * title 属性によるツールチップに委ねる。
 */
const makeWarningRow = (params: {
  rowNum: number;
  language: string;
  content: string;
  reason?: WarningRow['reason'];
  soundsLike?: string;
}): WarningRow => ({
  rowNum: params.rowNum,
  language: params.language,
  content: params.content,
  ...(params.reason !== undefined ? { reason: params.reason } : {}),
  ...(params.soundsLike !== undefined ? { soundsLike: params.soundsLike } : {}),
});

/**
 * 生データ行を言語コード・単語の存在でバリデーションし、
 * 有効行と INVALID / UNSUPPORTED_LANG 警告行に分類する。
 */
const validateRows = (
  rows: ParsedRow[],
  supportedLanguages?: Set<string>,
): {
  valid: ParsedRow[];
  invalidRows: WarningRow[];
  unsupportedLangRows: WarningRow[];
} => {
  const invalidRows: WarningRow[] = [];
  const unsupportedLangRows: WarningRow[] = [];

  const valid = rows.filter((r) => {
    if (!r.language || !r.content) {
      let reason: WarningRow['reason'];

      if (!r.language && !r.content) {
        reason = INVALID_ROW_REASON.BOTH;
      } else if (!r.language) {
        reason = INVALID_ROW_REASON.NO_LANG;
      } else {
        reason = INVALID_ROW_REASON.NO_CONTENT;
      }

      invalidRows.push(
        makeWarningRow({
          rowNum: r.rowNum,
          language: r.language,
          content: r.content,
          reason,
        }),
      );

      return false;
    }

    if (supportedLanguages && !supportedLanguages.has(r.language)) {
      unsupportedLangRows.push(
        makeWarningRow({
          rowNum: r.rowNum,
          language: r.language,
          content: r.content,
        }),
      );

      return false; // 非対応言語の行はインポート対象から除外する
    }

    return true;
  });

  return { valid, invalidRows, unsupportedLangRows };
};

/**
 * `language:content` を複合キーとして重複行を排除する。
 */
const deduplicateRows = (
  rows: ParsedRow[],
): { deduped: ParsedRow[]; duplicateRows: WarningRow[] } => {
  const seen = new Set<string>();
  const deduped: ParsedRow[] = [];
  const duplicateRows: WarningRow[] = [];

  rows.forEach((r) => {
    const key = `${r.language}:${r.content}`;

    if (seen.has(key)) {
      duplicateRows.push(
        makeWarningRow({
          rowNum: r.rowNum,
          language: r.language,
          content: r.content,
        }),
      );
    } else {
      seen.add(key);
      deduped.push(r);
    }
  });

  return { deduped, duplicateRows };
};

/**
 * スクリプトごとの sounds_like 文字種バリデーションパターン。
 *
 * Unicode スクリプトプロパティ（\p{sc=...}）を使うことで、
 * 結合文字（声調記号・母音記号・濁点など）を個別に列挙せず正確に判定できる。
 * 手動で文字コード範囲を書くと「ベトナム語のラテン拡張追加（U+1E00-U+1EFF）が抜ける」
 * のような書き漏らしが起きるため、プロパティベースの判定を採用する。
 *
 * 方針（Speechmatics ドキュメントより）:
 *   "sounds_like only works with the main script for that language"
 *   "Japanese (ja) sounds_like only supports full width Hiragana or Katakana"
 *   上記に素直に従い、言語ごとの例外は設けない。
 *   参照: https://docs.speechmatics.com/speech-to-text/features/custom-dictionary
 *
 * 中黒（U+30FB）は sc=Common であり sc=Katakana に含まれないため、
 * japanese パターンは「ぽけ・とーく」を自動的に除外する。
 * ×（U+00D7）÷（U+00F7）も sc=Common のため latin パターンでは既定で除外される。
 * ピリオド（.）は Speechmatics 公式サンプル { "content": "CEO", "sounds_like": ["C.E.O."] }
 * で使われているため latin のみ明示的に許可する。sc=Common のため \p{sc=Latin} には含まれず、
 * 個別に追加が必要。参照: https://docs.speechmatics.com/speech-to-text/features/custom-dictionary
 *
 * 全角スペース・NBSP は解析前に半角スペースへ正規化済み。
 * 半角スペース（U+0020）は日本語を除くすべての言語で語の区切りとして許可する。
 */
const SCRIPT_PATTERNS: Record<GlossaryScript, RegExp> = {
  // 日本語は Speechmatics が「全角ひらがな・カタカナのみ」と明記しているため、半角スペースを含めない。
  // 空白を禁止する明文はないが、このチェックの目的は Speechmatics 側で無言に drop されるのを防ぐことであり、
  // 仕様が不明な場合は通さない側に倒す。
  // ー（U+30FC 長音符）・゛（U+309B）・゜（U+309C）は sc=Common のため明示的に追加している。
  // 中黒（U+30FB）も sc=Common だが、列挙しないことで除外を維持する。
  // 参照: https://docs.speechmatics.com/speech-to-text/features/custom-dictionary
  japanese: /^[\p{sc=Hiragana}\p{sc=Katakana}ー゛゜]+$/u,
  hangul: /^[\p{sc=Hangul} ]+$/u,
  cyrillic: /^[\p{sc=Cyrillic} '-]+$/u,
  greek: /^[\p{sc=Greek} '-]+$/u,
  thai: /^[\p{sc=Thai} ]+$/u,
  hebrew: /^[\p{sc=Hebrew} '-]+$/u,
  arabic: /^[\p{sc=Arabic} ]+$/u,
  devanagari: /^[\p{sc=Devanagari} ]+$/u,
  bengali: /^[\p{sc=Bengali} ]+$/u,
  tamil: /^[\p{sc=Tamil} ]+$/u,
  han: /^[\p{sc=Han} ]+$/u,
  latin: /^[\p{sc=Latin} '.-]+$/u,
};

/**
 * Speechmatics の additional_vocab 制限と文字種を検証する。
 *
 * content・soundsLike は呼び出し前に全角スペース・NBSP が半角へ正規化済み、
 * かつ NFC 正規化済み。語の分割は空白文字（半角スペース・タブ）で行う。
 *
 * 1 行あたりの判定順（違反があればエントリごと除外。部分的な切り取りは行わない）:
 * (a) content が 6 語超          → TOO_MANY_WORDS_CONTENT
 * (b) content 内の 1 語が 4000 文字超 → CONTENT_TOO_LONG
 * (c) 読みの文字種違反             → INVALID_SOUNDS_LIKE_JPN / INVALID_SOUNDS_LIKE_OTHER
 * (d) 読み 1 件が 6 語超          → TOO_MANY_WORDS_SOUNDS_LIKE（明細に該当の読み）
 * (e) 読み内の 1 語が 4000 文字超  → SOUNDS_LIKE_TOO_LONG（明細に問題のある読みをそのまま設定）
 */
const validateSpeechmaticsLimits = (
  rows: ParsedRow[],
): {
  cleaned: ParsedRow[];
  invalidSoundsLikeJpnRows: WarningRow[];
  invalidSoundsLikeOtherRows: WarningRow[];
  tooManyWordsContentRows: WarningRow[];
  contentTooLongRows: WarningRow[];
  tooManyWordsSoundsLikeRows: WarningRow[];
  soundsLikeTooLongRows: WarningRow[];
} => {
  const invalidSoundsLikeJpnRows: WarningRow[] = [];
  const invalidSoundsLikeOtherRows: WarningRow[] = [];
  const tooManyWordsContentRows: WarningRow[] = [];
  const contentTooLongRows: WarningRow[] = [];
  const tooManyWordsSoundsLikeRows: WarningRow[] = [];
  const soundsLikeTooLongRows: WarningRow[] = [];

  const cleaned: ParsedRow[] = [];

  rows.forEach((r) => {
    // (a) content が 6 語超 → エントリごと除外
    const contentWords = splitWords(r.content);

    if (contentWords.length > SPEECHMATICS_MAX_WORDS_PER_ELEMENT) {
      tooManyWordsContentRows.push(
        makeWarningRow({
          rowNum: r.rowNum,
          language: r.language,
          content: r.content,
        }),
      );

      return;
    }

    // (b) content 内の 1 語が 4000 文字超 → エントリごと除外
    const hasLongContentWord = contentWords.some(
      (w) => w.length > SPEECHMATICS_MAX_WORD_LENGTH,
    );

    if (hasLongContentWord) {
      contentTooLongRows.push(
        makeWarningRow({
          rowNum: r.rowNum,
          language: r.language,
          content: r.content,
        }),
      );

      return;
    }

    // 言語コードからスクリプトを引く。GLOSSARY_LANGUAGES に載っている言語しか
    // validateRows を通過しないため、未知コードはここには来ない。
    const script = SCRIPT_BY_LANGUAGE.get(r.language) ?? 'latin';
    const pattern = SCRIPT_PATTERNS[script];

    // (c)(d)(e) sounds_like を 1 件ずつ検証し、最初の違反でそのエントリを除外する
    // some() を使って最初の違反が見つかった時点で打ち切る（for...of は no-restricted-syntax）
    const hasViolation = r.soundsLike.some((s) => {
      // (c) 文字種チェック → JPN は専用警告、それ以外は汎用警告
      if (!pattern.test(s)) {
        const targetRows =
          script === 'japanese'
            ? invalidSoundsLikeJpnRows
            : invalidSoundsLikeOtherRows;

        targetRows.push(
          makeWarningRow({
            rowNum: r.rowNum,
            language: r.language,
            content: r.content,
            soundsLike: s,
          }),
        );

        return true;
      }

      const soundWords = splitWords(s);

      // (d) 読み 1 件が 6 語超 → エントリごと除外（明細に該当の読みを設定）
      if (soundWords.length > SPEECHMATICS_MAX_WORDS_PER_ELEMENT) {
        tooManyWordsSoundsLikeRows.push(
          makeWarningRow({
            rowNum: r.rowNum,
            language: r.language,
            content: r.content,
            soundsLike: s,
          }),
        );

        return true;
      }

      // (e) 読み内の 1 語が 4000 文字超 → エントリごと除外
      const hasLongSoundWord = soundWords.some(
        (w) => w.length > SPEECHMATICS_MAX_WORD_LENGTH,
      );

      if (hasLongSoundWord) {
        soundsLikeTooLongRows.push(
          makeWarningRow({
            rowNum: r.rowNum,
            language: r.language,
            content: r.content,
            soundsLike: s,
          }),
        );

        return true;
      }

      return false;
    });

    if (!hasViolation) {
      cleaned.push({ ...r });
    }
  });

  return {
    cleaned,
    invalidSoundsLikeJpnRows,
    invalidSoundsLikeOtherRows,
    tooManyWordsContentRows,
    contentTooLongRows,
    tooManyWordsSoundsLikeRows,
    soundsLikeTooLongRows,
  };
};

/**
 * 言語ごとに上限を適用し、超過行を OVERFLOW 警告として除外する。
 */
const applyLangLimit = (
  rows: ParsedRow[],
  limit = MAX_ENTRIES_PER_LANGUAGE,
): { entries: GlossaryRow[]; overflowRows: WarningRow[] } => {
  const langCounts = new Map<string, number>();
  const entries: GlossaryRow[] = [];
  const overflowRows: WarningRow[] = [];

  rows.forEach((r) => {
    const count = langCounts.get(r.language) ?? 0;

    if (count >= limit) {
      overflowRows.push(
        makeWarningRow({
          rowNum: r.rowNum,
          language: r.language,
          content: r.content,
        }),
      );
    } else {
      langCounts.set(r.language, count + 1);
      entries.push({
        language: r.language,
        content: r.content,
        soundsLike: r.soundsLike,
      });
    }
  });

  return { entries, overflowRows };
};

/**
 * 用語集 CSV テキストをパースし、有効なエントリと警告を返す。
 *
 * 処理の流れ:
 * 1. BOM 除去・改行正規化
 * 2. デリミタ自動検出（カンマ／タブ）
 * 3. ヘッダー行の必須チェック（TEMPLATE_HEADER_ROWS と完全一致しなければ
 *    headerError: true を返して中断。空 CSV は除く）
 * 4. 全角スペース・NBSP を半角へ正規化し、NFC 正規化（content・soundsLike の各要素。
 *    全角スペースは Speechmatics に半角で渡すため、NFC 正規化は NFD 入力の結合文字が
 *    Unicode スクリプトプロパティ判定（SCRIPT_PATTERNS）を外れるのを防ぐため）
 * 5. バリデーション（言語コード・単語の存在チェック、非対応言語の除外）
 * 6. 重複排除（`language:content` 複合キー、先勝ち）
 * 7. Speechmatics 制限チェック（content 語数・文字数 → 文字種 → sounds_like 語数・文字数）
 * 8. 言語ごとに 1000 件の上限を適用
 *
 * 空の CSV（データ行ゼロ）をアップロードした場合は entries: [] を返し、
 * 呼び出し元で用語集の全削除（初期化）として扱う。
 *
 * @param text - CSV ファイルの生テキスト（BOM 除去済みでなくても可）
 * @param supportedLanguages - 対応言語コードの Set。未指定の場合は言語コードを検証しない。
 */
export const parseCsv = (
  text: string,
  supportedLanguages?: Set<string>,
): {
  entries: GlossaryRow[];
  warnings: UploadWarning[];
  dataRowCount: number;
  /** 1行目がテンプレートのヘッダーと一致しなかった（空CSVを除く） */
  headerError: boolean;
} => {
  // BOM 除去 → 行に分割 → 元ファイルの行番号（1始まり）を付与 → 空行を除去
  // 採番を filter より前に行うことで、途中に空行があっても行番号がずれない
  // 行全体を trim すると、タブ区切りで言語列が空の行（例: "\tPocketalk"）の
  // 先頭 TAB が失われ、language='Pocketalk' と誤ってパースされる。
  // trim を使った空行判定は '\t' を空行扱いしてしまうため、
  // 完全に空な文字列（真の空行）のみを除去する。
  const rawLines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line, i) => ({ line, lineNum: i + 1 }))
    .filter(({ line }) => line !== '');

  // データ行ゼロ（空 CSV）→ 用語集初期化として entries: [] を返す
  if (rawLines.length === 0)
    return { entries: [], warnings: [], dataRowCount: 0, headerError: false };

  // 先頭行からデリミタ（カンマ／タブ）を自動検出する
  const delimiter = detectDelimiter(rawLines[0].line);

  // 1行目が TEMPLATE_HEADER_ROWS（13言語テンプレート + 技術名形式）のいずれかと
  // 完全一致する場合のみヘッダーと判定し読み飛ばす。
  // 部分一致（列単位 OR/AND）にすると、content='単語' や language='language' を含む
  // 正常なデータ行を誤ってヘッダーと判定して黙って除外してしまうため。
  // Excel が行末を揃えるため末尾に空セルを追加することがあるので、
  // 末尾の空列だけ除去してから比較する。
  const firstCols = splitCsvLine(rawLines[0].line, delimiter).map((c) =>
    c.trim().toLowerCase(),
  );
  const normalizedFirstCols = [...firstCols];
  while (
    normalizedFirstCols.length > 0 &&
    normalizedFirstCols[normalizedFirstCols.length - 1] === ''
  ) {
    normalizedFirstCols.pop();
  }
  const isHeader = TEMPLATE_HEADER_ROWS.some(
    (h) =>
      normalizedFirstCols.length === h.length &&
      h.every((col, i) => normalizedFirstCols[i] === col),
  );

  // ヘッダー行は必須。1行目がテンプレートのヘッダーと一致しない CSV は
  // 用語集ファイルとして扱わず、パースせずにエラーを返す。
  // 全行が「対応していない言語コード」警告で埋まるより、
  // 1行で原因を示すほうがユーザーが直しやすいため。
  // ※ 空 CSV（真の空行のみ）は上で早期リターンしており、ここには来ない。
  //    「空 CSV = 用語集の全削除」の仕様を壊さないため。
  if (!isHeader) {
    return { entries: [], warnings: [], dataRowCount: 0, headerError: true };
  }

  const dataLines = rawLines.slice(1);
  // ヘッダー除外後の実データ行数（空CSV判定に使用）
  const dataRowCount = dataLines.length;

  // lineNum はすでに元ファイルの行番号なので headerOffset 不要
  // 全角スペース・NBSP を半角へ正規化し、さらに NFC 正規化を行ってから保存する。
  // NFC 正規化: 濁点・声調記号などが NFD（分解形）で入力されると結合文字が
  // sc=Inherited となり Unicode スクリプトプロパティの判定を外れるため。
  // 正規化 → trim の順に適用し、空になった soundsLike 要素は除去する。
  const parsed: ParsedRow[] = dataLines.map(({ line, lineNum }) => {
    const cols = splitCsvLine(line, delimiter);

    return {
      rowNum: lineNum,
      language: cols[0]?.trim() ?? '',
      content: normalizeSpaces(cols[1] ?? '')
        .normalize('NFC')
        .trim(),
      soundsLike: cols
        .slice(2)
        .map((s) => normalizeSpaces(s).normalize('NFC').trim())
        .filter(Boolean),
    };
  });

  const { valid, invalidRows, unsupportedLangRows } = validateRows(
    parsed,
    supportedLanguages,
  );
  const { deduped, duplicateRows } = deduplicateRows(valid);
  const {
    cleaned,
    invalidSoundsLikeJpnRows,
    invalidSoundsLikeOtherRows,
    tooManyWordsContentRows,
    contentTooLongRows,
    tooManyWordsSoundsLikeRows,
    soundsLikeTooLongRows,
  } = validateSpeechmaticsLimits(deduped);
  const { entries, overflowRows } = applyLangLimit(cleaned);

  const warnings: UploadWarning[] = [
    ...(duplicateRows.length > 0
      ? [{ type: UPLOAD_WARNING_TYPE.DUPLICATE, rows: duplicateRows }]
      : []),
    ...(invalidRows.length > 0
      ? [{ type: UPLOAD_WARNING_TYPE.MISSING_FIELD, rows: invalidRows }]
      : []),
    ...(overflowRows.length > 0
      ? [{ type: UPLOAD_WARNING_TYPE.OVERFLOW, rows: overflowRows }]
      : []),
    ...(unsupportedLangRows.length > 0
      ? [
          {
            type: UPLOAD_WARNING_TYPE.UNSUPPORTED_LANG,
            rows: unsupportedLangRows,
          },
        ]
      : []),
    ...(invalidSoundsLikeJpnRows.length > 0
      ? [
          {
            type: UPLOAD_WARNING_TYPE.INVALID_SOUNDS_LIKE_JPN,
            rows: invalidSoundsLikeJpnRows,
          },
        ]
      : []),
    ...(invalidSoundsLikeOtherRows.length > 0
      ? [
          {
            type: UPLOAD_WARNING_TYPE.INVALID_SOUNDS_LIKE_OTHER,
            rows: invalidSoundsLikeOtherRows,
          },
        ]
      : []),
    ...(tooManyWordsContentRows.length > 0
      ? [
          {
            type: UPLOAD_WARNING_TYPE.TOO_MANY_WORDS_CONTENT,
            rows: tooManyWordsContentRows,
          },
        ]
      : []),
    ...(contentTooLongRows.length > 0
      ? [
          {
            type: UPLOAD_WARNING_TYPE.CONTENT_TOO_LONG,
            rows: contentTooLongRows,
          },
        ]
      : []),
    ...(tooManyWordsSoundsLikeRows.length > 0
      ? [
          {
            type: UPLOAD_WARNING_TYPE.TOO_MANY_WORDS_SOUNDS_LIKE,
            rows: tooManyWordsSoundsLikeRows,
          },
        ]
      : []),
    ...(soundsLikeTooLongRows.length > 0
      ? [
          {
            type: UPLOAD_WARNING_TYPE.SOUNDS_LIKE_TOO_LONG,
            rows: soundsLikeTooLongRows,
          },
        ]
      : []),
  ];

  return { entries, warnings, dataRowCount, headerError: false };
};
